import {
    Debounce,
    DebounceSimple,
} from "@iunctim/utility-frontend/Resources/Public/Scripts/utilities/Debounce.js";

/**
 * Abstract header class to implement shared
 * functionality among different kind of headers
 */
export abstract class Header {
    protected header: HTMLElement | null = null;

    protected debouncedHeaderHeight: DebounceSimple<() => void>;
    protected debouncedHeaderBottom: DebounceSimple<() => void>;

    private headerHeightTicking: boolean = false;
    private headerBottomTicking: boolean = false;

    private lastHeaderHeight: number | null = null;
    private lastHeaderBottom: number | null = null;

    private static readonly HEADER_HEIGHT = "--header-height";
    private static readonly HEADER_BOTTOM = "--header-bottom";
    private static readonly SCROLL_MARGIN_TOP = "--scroll-margin-top";
    private static readonly STICKY_TOP_OFFSET = "--sticky-top-offset";
    private static readonly STICKY_TOP_OFFSET_VALUE = "32px";

    protected static readonly DROP_SHADOW_HEIGHT = 10;
    protected static readonly DEBOUNCING = 300;

    /**
     * The constructor to instantiate
     * the debounced callbacks
     */
    public constructor() {
        // Actually rather the "final" values, after a longer pause without layout shifts
        this.debouncedHeaderHeight = Debounce.simple(
            this.updateHeaderHeight,
            Header.DEBOUNCING,
        );
        this.debouncedHeaderBottom = Debounce.simple(
            this.updateHeaderBottom,
            Header.DEBOUNCING,
        );
    }

    /**
     * Method to register a resize observer in order
     * to keep the sticky-top CSS variable in sync for
     * the scroll-top-margin and sticky-top values
     *
     * @returns {void}
     */
    protected observeResizings(): void {
        if (!this.header) return;

        // Set the base values initially
        document.body.style.setProperty(Header.HEADER_HEIGHT, "0px");
        document.body.style.setProperty(Header.HEADER_BOTTOM, "0px");
        document.body.style.setProperty(
            Header.STICKY_TOP_OFFSET,
            Header.STICKY_TOP_OFFSET_VALUE,
        );

        // Set the scroll-margin-top calculation
        const headerBottom = `max(calc(var(${Header.HEADER_BOTTOM}) + var(--sticky-content-height, 0px)), 0px)`;

        document.body.style.setProperty(
            Header.SCROLL_MARGIN_TOP,
            `calc(${headerBottom} + var(${Header.STICKY_TOP_OFFSET}))`,
        );

        // Track the height of the header when the header resizes
        const observer = new ResizeObserver(() => {
            this.scheduleHeaderHeightUpdate();
            this.scheduleHeaderBottomUpdate();

            this.debouncedHeaderHeight();
            this.debouncedHeaderBottom();
        });
        observer.observe(this.header);

        // Track the header
        // bottom during resizing
        const updateBottoms = () => {
            this.scheduleHeaderBottomUpdate();
            this.debouncedHeaderBottom();
        };

        window.addEventListener("resize", updateBottoms, { passive: true });
        window.addEventListener("scroll", updateBottoms, { passive: true });

        // Evaluate the initial height
        // and bottom of the header
        this.updateHeaderHeight();
        this.updateHeaderBottom();
    }

    /**
     * Method to schedule the height update
     * at most once per animation frame
     *
     * @returns {void}
     */
    protected scheduleHeaderHeightUpdate = (): void => {
        if (this.headerHeightTicking) return;
        this.headerHeightTicking = true;

        requestAnimationFrame(() => {
            this.updateHeaderHeight();
            this.headerHeightTicking = false;
        });
    };

    /**
     * Method to schedule the bottom update
     * at most once per animation frame
     *
     * @returns {void}
     */
    protected scheduleHeaderBottomUpdate = (): void => {
        if (this.headerBottomTicking) return;
        this.headerBottomTicking = true;

        requestAnimationFrame(() => {
            this.updateHeaderBottom();
            this.headerBottomTicking = false;
        });
    };

    /**
     * Method to update the height of the header
     * variable when the size of the header changes
     *
     * @returns {void}
     */
    protected updateHeaderHeight = (): void => {
        if (!this.header) return;

        const height = this.header.offsetHeight;
        if (height === this.lastHeaderHeight) return;

        this.lastHeaderHeight = height;
        document.body.style.setProperty(Header.HEADER_HEIGHT, `${height}px`);
    };

    /**
     * Method to update the bottom of the header
     * variable when changes could occur through
     * scrolling and resizing of the viewport
     *
     * @returns {void}
     */
    protected updateHeaderBottom = (): void => {
        if (!this.header) return;

        const bottom = Math.round(
            this.header.getBoundingClientRect().bottom ?? 0,
        );
        if (bottom === this.lastHeaderBottom) return;

        this.lastHeaderBottom = bottom;
        document.body.style.setProperty(Header.HEADER_BOTTOM, `${bottom}px`);
    };
}
